// Document upload operations for the EXT supplier portal.
//
// JSON writes that go through the EXT proxy chain
// (browser → bldtext-fn /ProxiesV2 → bldt-api-fn /proxiesV2 → bldt-ui-fn
// /api/v2/{endpoint}). Reads are still done with useFetch; this module
// is write-only.

import { PostDocumentPayload, UploadMilestoneInvoicePayload } from '../@types';
import { uploadSupplierDocument } from './ai';
import { jsonRequest } from '../utils/request';

const API_URL = process.env.REACT_APP_API_URL;

// Helper used by FieldDocument — uploads a file to ai-fn and returns the
// new document id as a string. The condition / section / field IDs are
// optional but should always be passed when the upload originates from a
// requirement field, so the blob's tags carry enough lineage for bldt-ui
// to surface the file back against the same field at prepare-payment time.
export const uploadFieldDocument = async (
	file: File,
	contractId: number,
	projectId: number,
	supplierEntityId: number | undefined,
	docType: string,
	location?: {
		conditionId?: number;
		sectionId?: number | string;
		fieldId?: number | string;
	}
): Promise<{ documentId: string; documentName: string }> => {
	const res = await uploadSupplierDocument({
		file,
		docType,
		contractId,
		projectId,
		supplierEntityId,
		conditionId: location?.conditionId,
		sectionId: location?.sectionId,
		fieldId: location?.fieldId,
	});
	if (res.document_id == null) {
		throw new Error('Upload did not return a document id');
	}
	return { documentId: String(res.document_id), documentName: file.name };
};

// Per-milestone invoice upload. The invoice file is streamed through
// uploadSupplierDocument first, then a single invoice row is POSTed
// referencing the uploaded document.
export const uploadMilestoneInvoice = async (
	payload: UploadMilestoneInvoicePayload & {
		projectId: number;
		supplierEntityId?: number;
		supplierName: string;
		currency: string;
		amount?: number | null;
		invoiceNumber?: string | null;
		invoiceDate?: string | null;
	}
): Promise<unknown> => {
	const invoice = await uploadSupplierDocument({
		file: payload.invoiceFile,
		docType: 'invoice',
		contractId: payload.contractId,
		projectId: payload.projectId,
		supplierEntityId: payload.supplierEntityId,
	});

	// Attachment metadata follows the shared invoice convention
	// ({ id, name, type, size, uploadedAt, uploadedBy }) so a supplier-portal
	// invoice record is identical to one created from the main app, and the
	// document viewer resolves the file by the AI document id.
	const attachments = [
		{
			id: invoice.document_id != null ? Number(invoice.document_id) : 0,
			name: payload.invoiceFile.name,
			type: payload.invoiceFile.type,
			size: payload.invoiceFile.size,
			uploadedAt: new Date().toISOString(),
			uploadedBy: payload.supplierName,
		},
	];

	const body = {
		amount: payload.amount ?? null,
		attachments,
		contract: payload.contractId,
		currency: payload.currency,
		date: payload.invoiceDate ?? null,
		milestoneNumber: payload.milestoneNumber,
		milestoneName: payload.milestoneName,
		num: payload.invoiceNumber ?? null,
		source: 'SUPPLIER_PORTAL',
		supplier: payload.supplierEntityId ?? null,
	};

	return jsonRequest(`${API_URL}/api/ProxiesV2/invoices`, {
		method: 'POST',
		body,
	});
};

export const postDocuments = async ({
	file,
	contractId,
	invoiceId,
	projectId,
}: PostDocumentPayload): Promise<void> => {
	const token = sessionStorage.getItem('token');
	const formData = new FormData();
	formData.append('file', file, file.name);

	if (contractId !== undefined) {
		formData.append('contract', String(contractId));
	}
	if (invoiceId !== undefined) {
		formData.append('invoice', String(invoiceId));
	}
	if (projectId !== undefined) {
		formData.append('project', String(projectId));
	}

	const response = await fetch(`${API_URL}/api/ProxiesV2/documents`, {
		method: 'POST',
		headers: {
			Authorization: `Bearer ${token}`,
			apikey: process.env.REACT_APP_APIKEY || '',
		},
		body: formData,
	});

	const json = await response.json().catch(() => ({}));

	if (!response.ok) {
		throw new Error(json?.error?.message || `Request failed: ${response.status}`);
	}
};
